import React, { useEffect, useMemo, useState } from 'react';
import {
  Card,
  Grid,
  Icon,
  Tabs,
  Typography,
} from '@statsbomb/kitbag-components';
import { useQuery, useReactiveVar } from '@apollo/client';
import { CSSTransition, SwitchTransition } from 'react-transition-group';
import { useDistinctTeamColors } from '../../../utils/hooks/useDistinctTeamColors';
import useQueryString from '../../../utils/hooks/useQueryString';
import { mf_Games, mf_Teams } from '../../../apollo';
import { GET_GAMES } from '../../../components/Header/getGames';
import { GET_GAME_OVERVIEW, GET_GAME_DRAWN_PENALTIES } from './GetGameOverview';
import { KitbagPageGridHolder } from '../../../components/Layout/Layout.styles';
import PageHeader from '../../../components/PageHeader/PageHeader';
import { PAGE_HEADER_DISPLAY } from '../../../components/PageHeader/PageHeader.constants';
import { OVERVIEW_TABS } from './GameOverview.constants';
import PlayByPlayPanel from './PlayByPlayPanel';
import ScoringPlays from './ScoringPlays';
import Scoreboard from './Scoreboard';
import EpaRaceChart from './EpaRaceChart';
import { resolveDrawnPenaltyPlayer } from './PlaysGrid.dataManipulation';
import TrancheStatus from '../../../components/TrancheStatus/TrancheStatus';
import {
  TRANCHE_MODE,
  getStatusesFromGame,
} from '../../../components/TrancheStatus/TrancheStatus.dataManipulation';
import GameVideoModal from './GameVideoModal';
import {
  TabsWrap,
  EmptyState,
  PlayByPlayCardWrap,
  SummaryLayout,
  SummaryLeftColumn,
  SummaryRightColumn,
  TabContentTransition,
} from './GameOverview.styles';

function GameOverview() {
  // While the team -> game cascade resolves, the Header sets mf_Games to null
  // on team change and only fills it once the new team's games have loaded.
  // Skip the query in that window (a gameless query returns empty fast and
  // would flash an empty table) and treat it as loading instead.
  const selectedGameId = useReactiveVar(mf_Games);
  const selectedTeamId = useReactiveVar(mf_Teams);

  // notifyOnNetworkStatusChange so `loading` is reported on the refetch that
  // fires when the team/game selection changes (otherwise Apollo keeps the
  // previous game's rows on screen instead of showing the loader). Same option
  // the other filter-driven table pages use (League TeamStats/PlayerStats).
  const { data, loading, error, refetch } = useQuery(GET_GAME_OVERVIEW, {
    skip: !selectedGameId,
    notifyOnNetworkStatusChange: true,
  });

  const { data: drawnPenaltyData } = useQuery(GET_GAME_DRAWN_PENALTIES, {
    skip: !selectedGameId,
  });

  // The selected team's games (same query the Header/Layout runs, so this hits
  // the Apollo cache rather than adding a request). It lets us tell "no game
  // resolved yet" apart from "this team genuinely has no games", so we can show
  // a proper empty state instead of an endless loader. notifyOnNetworkStatus so
  // gamesLoading is true while switching teams (avoids a stale-empty flash when
  // leaving a gameless team).
  const { data: gamesData, loading: gamesLoading } = useQuery(GET_GAMES, {
    notifyOnNetworkStatusChange: true,
    variables: { includeT1Games: true },
  });
  const teamHasNoGames =
    !selectedGameId &&
    !gamesLoading &&
    (gamesData?.games?.edges?.length ?? 0) === 0;

  // A game is selected but its plays have not arrived yet -> still loading.
  // (Never "loading" while the team has no games - that's the empty state.)
  const isLoading = !teamHasNoGames && (loading || !selectedGameId);

  const [tabParam, setActiveTab] = useQueryString('tab', 'summary');
  // An unknown ?tab= value falls back to Summary so a tab is always selected.
  const activeTab = OVERVIEW_TABS.some((t) => t.value === tabParam)
    ? tabParam
    : 'summary';
  const [videoPlayIds, setVideoPlayIds] = useState([]);
  const [videoOpen, setVideoOpen] = useState(false);

  // Switching team/game reuses this same component instance, so clear the
  // video modal whenever the selected game changes - otherwise a stale modal
  // tied to the previous game carries over.
  useEffect(() => {
    setVideoPlayIds([]);
    setVideoOpen(false);
  }, [selectedGameId]);

  // playId -> "Name1, Name2" for players who drew a penalty on that play.
  // The server already filters to drawnPenalty=true rows, so every returned
  // node is a drawn-penalty player-play.
  const drawnPenaltyByPlayId = useMemo(() => {
    const edges = drawnPenaltyData?.playerPlays?.edges ?? [];
    const map = new Map();
    edges.forEach(({ node }) => {
      const playId = node.play?.id;
      if (!playId) return;
      const playerName = node.player?.knownName || node.player?.name;
      if (!playerName) return;
      const existing = map.get(playId);
      map.set(playId, existing ? `${existing}, ${playerName}` : playerName);
    });
    return map;
  }, [drawnPenaltyData]);

  const plays = useMemo(() => {
    const nodes =
      data?.plays?.edges?.map((e) => e.node).filter((p) => p.drive) ?? [];
    return nodes.map((play) => ({
      ...play,
      drawnPenaltyPlayer: resolveDrawnPenaltyPlayer(
        play,
        drawnPenaltyByPlayId.get(play.id),
      ),
    }));
  }, [data, drawnPenaltyByPlayId]);

  const game = plays[0]?.game ?? null;
  const trancheStatuses = getStatusesFromGame(game);

  // Mirror the Play by Play header: put the selected team (from the global
  // filter) on the left and the opponent on the right, in the same order
  // and with the same colour logic.
  const isHomeTeam = game?.homeTeam?.id === selectedTeamId;
  const selectedTeam = isHomeTeam ? game?.homeTeam : game?.awayTeam;
  const opponentTeam = isHomeTeam ? game?.awayTeam : game?.homeTeam;

  const selectedColors = useMemo(
    () => ({
      primary: selectedTeam?.colorPrimary,
      secondary: selectedTeam?.colorSecondary,
      tertiary: selectedTeam?.colorTertiary,
    }),
    [selectedTeam],
  );
  const opponentColors = useMemo(
    () => ({
      primary: opponentTeam?.colorPrimary,
      secondary: opponentTeam?.colorSecondary,
      tertiary: opponentTeam?.colorTertiary,
    }),
    [opponentTeam],
  );
  const { selectedTeamColor, opponentTeamColor } = useDistinctTeamColors(
    selectedColors,
    opponentColors,
  );

  // When a game is T1-only, advanced stats like scrimmageEpa are not available.
  // Strip them from the plays passed to summary components so the charts and
  // tables reflect what a real T1 game would return from the API. (The full
  // `plays` array with EPA intact is still used for the Play by Play panel,
  // which handles its own data availability.)
  const summaryPlays = useMemo(
    () =>
      game?.t1DataOnly
        ? plays.map((p) => ({ ...p, scrimmageEpa: null }))
        : plays,
    [plays, game?.t1DataOnly],
  );

  // PlayByPlayPanel calls onSelectDrive with a drive-row object whose
  // `plays` array contains play objects with numeric `id` fields.
  const onSelectDrive = (drive) => {
    const ids = (drive.plays ?? []).map((p) => String(p.id));
    setVideoPlayIds(ids);
    setVideoOpen(true);
  };

  // PlayByPlayPanel calls onSelectPlay with a play object that has an `id`.
  const onSelectPlay = (play) => {
    setVideoPlayIds([String(play.id)]);
    setVideoOpen(true);
  };

  const pbpContent = teamHasNoGames ? (
    <PlayByPlayCardWrap>
      <Card>
        <EmptyState>
          <Icon variant="Info" size="regular" />
          <Typography variant="headingLarge">No Games Available</Typography>
          <Typography variant="bodyRegular">
            No games are available for this team and season.
          </Typography>
        </EmptyState>
      </Card>
    </PlayByPlayCardWrap>
  ) : (
    <PlayByPlayPanel
      game={game}
      onSelectDrive={onSelectDrive}
      onSelectPlay={onSelectPlay}
    />
  );

  return (
    <KitbagPageGridHolder>
      <Grid container={false} page>
        <Grid item xs={12}>
          <PageHeader
            href="/game/overview/:leagues/:seasons/:teams/:games"
            rootPage="Game"
            activePage="Overview"
            titleType={PAGE_HEADER_DISPLAY.GAME}
            game={game}
          >
            {trancheStatuses && (
              <TrancheStatus
                mode={TRANCHE_MODE.TWO}
                statuses={trancheStatuses}
              />
            )}
          </PageHeader>

          {/* One tab bar for both tabs, outside the transition so it stays
              put while the content fades. It sits above the content
              containers because those differ in width per tab. */}
          <TabsWrap>
            <Tabs
              tabs={OVERVIEW_TABS}
              activeTab={activeTab}
              onTabChange={(e, tab) => setActiveTab(tab.value)}
              testId="game-overview-tabs"
            />
          </TabsWrap>

          <SwitchTransition mode="out-in">
            <CSSTransition
              key={
                activeTab === 'summary' && !teamHasNoGames ? 'summary' : 'pbp'
              }
              timeout={{ enter: 200, exit: 150 }}
              classNames="tab-fade"
            >
              <TabContentTransition>
                {activeTab === 'summary' && !teamHasNoGames ? (
                  <SummaryLayout>
                    <SummaryLeftColumn>
                      <ScoringPlays
                        plays={summaryPlays}
                        game={game}
                        loading={isLoading}
                        error={error}
                        onRetry={() => refetch()}
                        onSelectPlay={onSelectPlay}
                        isHomeTeam={isHomeTeam}
                        selectedTeamColor={selectedTeamColor}
                        opponentTeamColor={opponentTeamColor}
                      />
                    </SummaryLeftColumn>
                    <SummaryRightColumn>
                      <Scoreboard
                        game={game}
                        plays={summaryPlays}
                        loading={isLoading}
                        isHomeTeam={isHomeTeam}
                        selectedTeamColor={selectedTeamColor}
                        opponentTeamColor={opponentTeamColor}
                      />
                      <EpaRaceChart
                        plays={summaryPlays}
                        game={game}
                        isHomeTeam={isHomeTeam}
                        selectedTeamColor={selectedTeamColor}
                        opponentTeamColor={opponentTeamColor}
                      />
                    </SummaryRightColumn>
                  </SummaryLayout>
                ) : (
                  pbpContent
                )}
              </TabContentTransition>
            </CSSTransition>
          </SwitchTransition>
        </Grid>
      </Grid>

      <GameVideoModal
        isOpen={videoOpen}
        onClose={() => setVideoOpen(false)}
        playIds={videoPlayIds}
      />
    </KitbagPageGridHolder>
  );
}

export default GameOverview;
